import React, { useMemo } from "react";
import { StreamingProvider } from "@switcherstudio/switcher-api-client";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { PlatformId } from "views/page-content/platforms/types";
import { Link } from "components/buttons/Link";
import { Trans, useTranslation } from "react-i18next";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useCombineLoading } from "hooks/useCombineLoading";
import { Spinner } from "components/spinners/Spinner";
import { DestinationCard } from "components/modal/NewLivestreamModal/steps/SelectDestination/destinations/DestinationCard";
import { DestinationDisplay } from "components/modal/NewLivestreamModal/steps/SelectDestination/destinations/DestinationDisplay";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { updateGeneralForm } from "store/platforms/slice";
import PencilIcon from "assets/icons/pencil-simple.svg?react";
import { AppDispatch } from "store/store";
// Reuse the wizard's own layout/card styles instead of duplicating them.
import styles from "components/modal/NewLivestreamModal/steps/SelectDestination/index.module.scss";

interface SelectExternalDestinationStepProps {
    // Destinations already connected to this broadcast - excluded from picking again.
    existingDestinationIds: Set<string>;
    setSelectedPlatform: (platform: PlatformId | null) => void;
    advanceToPlatformSettings: (destination?: DestinationDescriptor) => void;
}

// A trimmed fork of NewLivestreamModal's SelectDestinationStep (sc-56801).
export const SelectExternalDestinationStep = ({
    existingDestinationIds,
    setSelectedPlatform,
    advanceToPlatformSettings
}: SelectExternalDestinationStepProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const user = useSelector((state: RootState) => state.user);

    const platforms = useSelector((s: RootState) => s.platforms);
    const generalForm = platforms.general.generalForm;

    const newlySelectedDestinations = useMemo(
        () =>
            generalForm.selectedSimulcastDestinations.filter(
                (d) => !existingDestinationIds.has(d.id)
            ),
        [generalForm.selectedSimulcastDestinations, existingDestinationIds]
    );

    const userHasHitMaxDests = useMemo(() => {
        const userMaxDests =
            user.userInfo?.Claims?.Details?.FeatureLimitClaims?.[
                "cloudflaresimulcasting:maxdestinations"
            ];

        const numberOfSelectedDests =
            generalForm.selectedSimulcastDestinations.filter(
                (d) => d.platform !== PlatformId.VideoPlayer
            ).length;

        return (
            userMaxDests &&
            userMaxDests > 0 &&
            numberOfSelectedDests >= userMaxDests
        );
    }, [user.userInfo, generalForm.selectedSimulcastDestinations]);

    const {
        transformedData: streamingProviders,
        loading: streamingProvidersLoading
    } = useSwitcherClient(
        (client) => client.streamingProviders_GetStreamingProviders,
        {
            suppressErrorBoundary: true,
            requestImmediately: true,
            transformResponseData: ({ originalResponse }) => {
                const supportedPlatforms = [
                    PlatformId.Facebook,
                    PlatformId.Youtube,
                    PlatformId.Twitch
                ];
                return originalResponse.StreamingProviders.filter((p) =>
                    supportedPlatforms.includes(p.Id as PlatformId)
                );
            }
        }
    );

    const { transformedData: rtmpChannels, loading: rtmpChannelsLoading } =
        useSwitcherClient(
            (client) =>
                client.switcherStreamSettings_GetAllSwitcherStudioSettings,
            {
                suppressErrorBoundary: true,
                requestImmediately: true,
                transformResponseData: ({ originalResponse }) => {
                    return originalResponse;
                }
            }
        );

    const availableExternalPlatforms = useMemo(() => {
        return [
            ...(streamingProviders || []),
            {
                Id: PlatformId.Rtmp,
                Name: "RTMP",
                IsLinked: !!rtmpChannels?.length
            }
        ].sort((a, b) => Number(b.IsLinked) - Number(a.IsLinked));
    }, [streamingProviders, rtmpChannels]);

    const isPlatformDisabled = (p: StreamingProvider) => {
        return !p.IsLinked;
    };

    const platformClickHandler = (platform: StreamingProvider) => {
        setSelectedPlatform(platform.Id as PlatformId);
        advanceToPlatformSettings();
    };

    const removeDestination = (destination: DestinationDescriptor) => {
        dispatch(
            updateGeneralForm({
                selectedSimulcastDestinations:
                    generalForm.selectedSimulcastDestinations.filter(
                        (d) => d.id !== destination.id
                    )
            })
        );
    };

    const loading = useCombineLoading(
        streamingProvidersLoading,
        rtmpChannelsLoading
    );

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className={styles["select-destination-step"]}>
            {newlySelectedDestinations.length > 0 && (
                <section>
                    <h6>{t("simulcast:selected-destinations")}</h6>
                    <DestinationDisplay
                        destinations={newlySelectedDestinations}
                        actions={[
                            {
                                type: "icon",
                                onClick: (destination) => {
                                    setSelectedPlatform(
                                        destination.platform as PlatformId
                                    );
                                    advanceToPlatformSettings(destination);
                                },
                                element: <PencilIcon />
                            },
                            {
                                type: "remove",
                                onClick: (destination) =>
                                    removeDestination(destination),
                                element: <>{t("buttons:remove")}</>
                            }
                        ]}
                    />
                </section>
            )}
            <section>
                <h6>{t("simulcast:select-simulcast-destination")}</h6>
                <small
                    className={
                        userHasHitMaxDests ? styles["limit-warning"] : ""
                    }
                >
                    <p>
                        {userHasHitMaxDests ? (
                            t("simulcast:limit-hit")
                        ) : (
                            <Trans
                                i18nKey="simulcast:connect-platform-cta"
                                components={{
                                    link1: <Link to="/manage-platforms" />
                                }}
                            />
                        )}
                    </p>
                </small>
                <div className={styles["destinations-wrapper"]}>
                    {availableExternalPlatforms?.map((ap, i) => {
                        return (
                            <DestinationCard
                                platform={ap}
                                key={ap.Id ?? i}
                                isDisabled={
                                    isPlatformDisabled(ap) || userHasHitMaxDests
                                }
                                clickHandler={() => platformClickHandler(ap)}
                            />
                        );
                    })}
                </div>
            </section>
        </div>
    );
};
